import React from "react";
import { TableRowActionsWrapper } from "@/components/shared/UniversalCRMTable/components/SimpleTable/TableRowActionsWrapper";
import { useTaskHooks } from "@/lib/hooks/api/shared/graphql-hooks";
import { getEntityTableConfig } from "@/lib/entity-registry";
import type { ContactTaskRow } from "@/lib/types/entity-rows";

// Codegen entity type, matching the `task: ContactTaskRow` prop below and
// `useTaskHooks(): EntityHooks<ContactTaskRow>` — so `detailActions` is
// `DetailAction<ContactTaskRow>[]` and neither the actions nor the hooks need a
// cast at the `TableRowActionsWrapper` boundary.
// See ENTITY_HOOKS_USE_CODEGEN_TYPES.
const TASK_CONFIG = getEntityTableConfig<ContactTaskRow>("tasks");

interface UnifiedTaskRowActionsProps {
  task: ContactTaskRow;
  compact?: boolean;
  refetch?: () => void | Promise<void>;
}

/**
 * UnifiedTaskRowActions - Single source of truth for task row actions
 *
 * This component ensures that both horizontal cards and table rows
 * display the exact same actions by using the centralized TableRowActionsWrapper
 * with the task's detailActions from TASK_CONFIG.
 *
 * Used by:
 * - HorizontalCard (for card view actions)
 * - TableRow (for table view actions)
 * - Any other component that needs task row actions
 *
 * Benefits:
 * - Single source of truth for all task row actions
 * - Consistent behavior across all views
 * - Easy to maintain and update actions in one place
 */
export function UnifiedTaskRowActions({
  task,
  compact = true,
  refetch,
}: UnifiedTaskRowActionsProps) {
  const taskHooks = useTaskHooks();

  // Use the detail actions from TASK_CONFIG
  // These are defined in taskDetailActions and imported into TASK_CONFIG
  const detailActions = TASK_CONFIG.detailActions || [];

  return (
    <TableRowActionsWrapper
      item={task}
      actions={detailActions}
      hooks={taskHooks}
      entityName="tasks"
      compact={compact}
      refetch={refetch}
    />
  );
}
